import { type userType } from '../common-types';

export interface KnowledgeCardUserSearchVariables {
	userQuery: string;
	siteId: string;
}

export interface KnowledgeCardSearchUserType {
	name: string;
	id: string;
	avatarUrl: string | null;
	title: string | null;
	zoneInfo: string | null;
	location: string | null;
}

export interface KnowledgeCardUserSearchQueryType {
	knowledgeDiscovery: {
		searchUser: {
			users?: [KnowledgeCardSearchUserType];
			message?: string | null;
		};
	};
}

export const KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME = 'KnowledgeCardUserSearchQuery';
export const KnowledgeCardUserSearchQuery: '\nquery KnowledgeCardUserSearchQuery($userQuery: String!, $siteId: String!) {\n  knowledgeDiscovery {\n   \tsearchUser (\n        userQuery: $userQuery\n        siteId: $siteId\n    ) @optIn(to: "KnowledgeDiscovery Search User"){\n      ... on KnowledgeDiscoveryUsers {\n        users {\n            name\n            id\n            avatarUrl\n            title\n            zoneInfo\n            location\n        }\n      }\n      ... on QueryError {\n        message\n      }\n    }\n  }\n}\n' = `
query ${KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME}($userQuery: String!, $siteId: String!) {
  knowledgeDiscovery {
   	searchUser (
        userQuery: $userQuery
        siteId: $siteId
    ) @optIn(to: "KnowledgeDiscovery Search User"){
      ... on KnowledgeDiscoveryUsers {
        users {
            name
            id
            avatarUrl
            title
            zoneInfo
            location
        }
      }
      ... on QueryError {
        message
      }
    }
  }
}
`;

export interface recommendedUserType {
	id: string;
	user: userType;
}

export interface KnowledgeCardPrimaryUserDataQueryType {
	user: {
		id: string;
		picture: string;
		name: string;
		accountStatus: string;
		email?: string | null;
		zoneinfo?: string | null;
		extendedProfile?: {
			jobTitle?: string | null;
			department?: string | null;
			location?: string | null;
			organization?: string | null;
		};
	};
	smarts: {
		recommendedUser: recommendedUserType[];
	};
}

export interface KnowledgeCardPrimaryUserDataVariables {
	accountId: string;
	accountIdARI: string;
	tenantId: string;
	requestingUserId: string;
}

export const KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME = 'KnowledgeCardPrimaryUserDataQuery';
// we construct accountIdARI by appending "ari:cloud:identity::user/" + accountId
export const KnowledgeCardPrimaryUserDataQuery: '\n    query KnowledgeCardPrimaryUserDataQuery(\n        $accountId: String!\n        $accountIdARI: ID!\n        $tenantId: String!\n        $requestingUserId: String!\n        ) {\n        user(accountId: $accountIdARI) {\n            id\n            picture\n            name\n            accountStatus\n            ... on AtlassianAccountUser {\n                email\n                zoneinfo\n                extendedProfile {\n                    jobTitle\n                    department\n                    organization\n                    location\n                }\n            }\n        }\n        smarts {\n            recommendedUser(recommendationsQuery: {\n            context: {\n                userId: $accountId, tenantId: $tenantId\n            },\n            modelRequestParams: {\n                caller: "search-ai",\n                experience: "CgUserNearbyUser"\n            },\n            requestingUserId: $requestingUserId,\n            maxNumberOfResults: 8\n            }\n            ) {\n            id\n            user {\n                id\n                picture\n                name\n                accountStatus\n                ... on AtlassianAccountUser {\n                email\n                extendedProfile {\n                    jobTitle\n                    department\n                }\n                }\n            }\n            }\n        }\n    }\n' = `
    query ${KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME}(
        $accountId: String!
        $accountIdARI: ID!
        $tenantId: String!
        $requestingUserId: String!
        ) {
        user(accountId: $accountIdARI) {
            id
            picture
            name
            accountStatus
            ... on AtlassianAccountUser {
                email
                zoneinfo
                extendedProfile {
                    jobTitle
                    department
                    organization
                    location
                }
            }
        }
        smarts {
            recommendedUser(recommendationsQuery: {
            context: {
                userId: $accountId, tenantId: $tenantId
            },
            modelRequestParams: {
                caller: "search-ai",
                experience: "CgUserNearbyUser"
            },
            requestingUserId: $requestingUserId,
            maxNumberOfResults: 8
            }
            ) {
            id
            user {
                id
                picture
                name
                accountStatus
                ... on AtlassianAccountUser {
                email
                extendedProfile {
                    jobTitle
                    department
                }
                }
            }
            }
        }
    }
`;

export const KnowledgeCardPrimaryUserDataWithUserUserV5Query: '\n    query KnowledgeCardPrimaryUserDataQuery(\n        $accountId: String!\n        $accountIdARI: ID!\n        $tenantId: String!\n        $requestingUserId: String!\n        ) {\n        user(accountId: $accountIdARI) {\n            id\n            picture\n            name\n            accountStatus\n            ... on AtlassianAccountUser {\n                email\n                zoneinfo\n                extendedProfile {\n                    jobTitle\n                    department\n                    organization\n                    location\n                }\n            }\n        }\n        smarts {\n            recommendedUser(recommendationsQuery: {\n            context: {\n                userId: $accountId, tenantId: $tenantId\n            },\n            modelRequestParams: {\n                caller: "search-ai",\n                experience: "CgUserNearbyUser"\n                featureServiceModel: "cg_user_user_v5"\n            },\n            requestingUserId: $requestingUserId,\n            maxNumberOfResults: 8\n            }\n            ) {\n            id\n            user {\n                id\n                picture\n                name\n                accountStatus\n                ... on AtlassianAccountUser {\n                email\n                extendedProfile {\n                    jobTitle\n                    department\n                }\n                }\n            }\n            }\n        }\n    }\n' = `
    query ${KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME}(
        $accountId: String!
        $accountIdARI: ID!
        $tenantId: String!
        $requestingUserId: String!
        ) {
        user(accountId: $accountIdARI) {
            id
            picture
            name
            accountStatus
            ... on AtlassianAccountUser {
                email
                zoneinfo
                extendedProfile {
                    jobTitle
                    department
                    organization
                    location
                }
            }
        }
        smarts {
            recommendedUser(recommendationsQuery: {
            context: {
                userId: $accountId, tenantId: $tenantId
            },
            modelRequestParams: {
                caller: "search-ai",
                experience: "CgUserNearbyUser"
                featureServiceModel: "cg_user_user_v5"
            },
            requestingUserId: $requestingUserId,
            maxNumberOfResults: 8
            }
            ) {
            id
            user {
                id
                picture
                name
                accountStatus
                ... on AtlassianAccountUser {
                email
                extendedProfile {
                    jobTitle
                    department
                }
                }
            }
            }
        }
    }
`;
